import { useId, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { InlineFieldInput, InlineFieldTextarea } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/search-select'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import {
  useFileSafewareClaim,
  useSafewareClaimDraft,
  type SafewareClaimCreate,
  type SafewareClaimDraft,
  type SafewareProtectedUnit,
} from '@/api/safeware'

const UPDATE_OPTIONS = [
  { value: '2', label: 'Email' },
  { value: '1', label: 'Text' },
  { value: '3', label: 'Text & email' },
] as const

type ContactField =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone'
  | 'address_line1'
  | 'address_line2'
  | 'city'
  | 'state'
  | 'zip_code'

function errorDetail(error: unknown): string {
  const detail = (error as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (item as { msg?: string; loc?: unknown[] }).msg ?? '')
      .filter(Boolean)
      .join('; ')
  }
  return 'Safeware couldn’t be reached. Try again in a few minutes.'
}

/** File a Safeware claim for one covered unit, pre-filled from Safeware's coverage record (USA-1165). */
export function SafewareClaimSheet({
  orderUuid,
  unit,
  onClose,
}: {
  orderUuid: string
  unit: SafewareProtectedUnit
  onClose: () => void
}) {
  const { data: draft, isLoading, error } = useSafewareClaimDraft(unit.uuid)
  const fileClaim = useFileSafewareClaim(orderUuid)

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open && !fileClaim.isPending) onClose()
      }}
    >
      <SheetContent className="flex w-[min(96vw,560px)] flex-col gap-0 p-0 sm:max-w-none">
        <div className="shrink-0 space-y-1 border-b px-5 py-4 pr-12">
          <SheetTitle className="text-base">File Safeware claim</SheetTitle>
          <SheetDescription className="text-xs">
            {[unit.equipment_make, unit.equipment_model].filter(Boolean).join(' ')}
            {unit.serial_number ? ` · Serial ${unit.serial_number}` : ''}
            {unit.policy_number ? ` · Policy ${unit.policy_number}` : ''}
          </SheetDescription>
        </div>
        {draft ? (
          <ClaimForm
            draft={draft}
            busy={fileClaim.isPending}
            submitError={fileClaim.error ? errorDetail(fileClaim.error) : null}
            onCancel={onClose}
            onSubmit={(body) =>
              fileClaim.mutate(
                { unitUuid: unit.uuid, body },
                {
                  onSuccess: (claim) => {
                    toast.success(`Claim ${claim.claim_number} filed with Safeware`)
                    onClose()
                  },
                },
              )
            }
          />
        ) : isLoading ? (
          <div className="space-y-4 p-5" aria-busy="true" aria-label="Loading Safeware coverage">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : (
          <p role="alert" className="p-5 text-sm text-destructive">
            {errorDetail(error)}
          </p>
        )}
      </SheetContent>
    </Sheet>
  )
}

function ClaimForm({
  draft,
  busy,
  submitError,
  onCancel,
  onSubmit,
}: {
  draft: SafewareClaimDraft
  busy: boolean
  submitError: string | null
  onCancel: () => void
  onSubmit: (body: SafewareClaimCreate) => void
}) {
  const id = useId()
  const [eventType, setEventType] = useState<string>('')
  const [otherEvent, setOtherEvent] = useState('')
  const [occurred, setOccurred] = useState(draft.today)
  const [description, setDescription] = useState('')
  const [updates, setUpdates] = useState<string>(String(draft.notification_type))
  const [contact, setContact] = useState<Record<ContactField, string>>({
    first_name: draft.first_name,
    last_name: draft.last_name,
    email: draft.email,
    phone: draft.phone,
    address_line1: draft.address_line1,
    address_line2: draft.address_line2,
    city: draft.city,
    state: draft.state,
    zip_code: draft.zip_code,
  })
  const isOther = eventType === String(draft.other_event_type)
  const phoneDigits = contact.phone.replace(/\D/g, '')
  const missing = [
    !eventType && 'event',
    isOther && !otherEvent.trim() && 'kind of event',
    !occurred && 'date',
    !description.trim() && 'description',
    (!contact.first_name.trim() || !contact.last_name.trim()) && 'name',
    (updates === '2' || updates === '3') && !contact.email.trim() && 'email',
    (updates === '1' || updates === '3') && phoneDigits.length < 10 && 'phone',
    (!contact.address_line1.trim() || !contact.city.trim() || contact.state.trim().length !== 2) && 'address',
    contact.zip_code.replace(/\D/g, '').length < 5 && 'ZIP',
  ].filter(Boolean)

  function set(field: ContactField) {
    return (event: { target: { value: string } }) =>
      setContact((current) => ({ ...current, [field]: event.target.value }))
  }

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault()
        if (missing.length || busy) return
        onSubmit({
          reference_number: draft.reference_number,
          make: draft.make,
          model: draft.model,
          serial_number: draft.serial_number,
          ...contact,
          state: contact.state.trim().toUpperCase(),
          phone: phoneDigits || null,
          email: contact.email.trim() || null,
          notification_type: Number(updates),
          type_of_event: Number(eventType),
          other_type_of_event: isOther ? otherEvent.trim() : null,
          event_occurred: occurred,
          event_description: description.trim(),
        })
      }}
    >
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-5">
        {draft.notes.map((note) => (
          <p key={note} className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            {note}
          </p>
        ))}

        <fieldset className="space-y-4">
          <legend className="mb-3 text-sm font-semibold">What happened</legend>
          <Field label="Event" htmlFor={`${id}-event`}>
            <SearchSelect
              id={`${id}-event`}
              className="h-9 w-full"
              value={eventType || undefined}
              options={draft.event_types.map((option) => ({
                value: String(option.code),
                label: option.label,
              }))}
              onChange={(next) => next && setEventType(next)}
              placeholder="Choose Safeware’s event type"
              searchPlaceholder="Search event types"
            />
          </Field>
          {isOther && (
            <Field label="Kind of event" htmlFor={`${id}-other`}>
              <InlineFieldInput
                id={`${id}-other`}
                value={otherEvent}
                maxLength={50}
                onChange={(event) => setOtherEvent(event.target.value)}
              />
            </Field>
          )}
          <Field label="Date it happened" htmlFor={`${id}-date`}>
            <InlineFieldInput
              id={`${id}-date`}
              type="date"
              value={occurred}
              max={draft.today}
              onChange={(event) => setOccurred(event.target.value)}
              className="w-full sm:w-48"
            />
          </Field>
          <Field label="Description" htmlFor={`${id}-description`}>
            <InlineFieldTextarea
              id={`${id}-description`}
              value={description}
              rows={4}
              maxLength={4000}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-semibold">Customer updates</legend>
          <ReviewChoiceGroup
            label="Customer updates"
            value={updates}
            options={UPDATE_OPTIONS}
            onChange={setUpdates}
            disabled={busy}
          />
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="mb-3 text-sm font-semibold">Customer</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" htmlFor={`${id}-first`}>
              <InlineFieldInput id={`${id}-first`} value={contact.first_name} maxLength={15} onChange={set('first_name')} />
            </Field>
            <Field label="Last name" htmlFor={`${id}-last`}>
              <InlineFieldInput id={`${id}-last`} value={contact.last_name} maxLength={15} onChange={set('last_name')} />
            </Field>
            <Field label="Email" htmlFor={`${id}-email`}>
              <InlineFieldInput id={`${id}-email`} type="email" value={contact.email} maxLength={50} onChange={set('email')} />
            </Field>
            <Field label="Phone" htmlFor={`${id}-phone`}>
              <InlineFieldInput id={`${id}-phone`} type="tel" value={contact.phone} maxLength={20} onChange={set('phone')} />
            </Field>
          </div>
          <Field label="Address" htmlFor={`${id}-address1`}>
            <InlineFieldInput
              id={`${id}-address1`}
              value={contact.address_line1}
              maxLength={35}
              onChange={set('address_line1')}
            />
          </Field>
          <Field label="Address line 2" htmlFor={`${id}-address2`}>
            <InlineFieldInput
              id={`${id}-address2`}
              value={contact.address_line2}
              maxLength={35}
              onChange={set('address_line2')}
            />
          </Field>
          <div className="grid grid-cols-[minmax(0,1fr)_4.5rem_6rem] gap-3">
            <Field label="City" htmlFor={`${id}-city`}>
              <InlineFieldInput id={`${id}-city`} value={contact.city} maxLength={25} onChange={set('city')} />
            </Field>
            <Field label="State" htmlFor={`${id}-state`}>
              <InlineFieldInput id={`${id}-state`} value={contact.state} maxLength={2} onChange={set('state')} />
            </Field>
            <Field label="ZIP" htmlFor={`${id}-zip`}>
              <InlineFieldInput
                id={`${id}-zip`}
                inputMode="numeric"
                value={contact.zip_code}
                maxLength={10}
                onChange={set('zip_code')}
              />
            </Field>
          </div>
        </fieldset>
      </div>

      <div className="shrink-0 space-y-3 border-t px-5 py-4">
        {submitError && (
          <p role="alert" className="text-sm text-destructive">
            {submitError}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {missing.length > 0 && (
            <p className="mr-auto text-xs text-muted-foreground">Needs {missing.join(', ')}</p>
          )}
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <LoadingButton type="submit" loading={busy} loadingText="Filing..." disabled={missing.length > 0}>
            File claim
          </LoadingButton>
        </div>
      </div>
    </form>
  )
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs font-medium text-muted-foreground" htmlFor={htmlFor}>
        {label}
      </Label>
      {children}
    </div>
  )
}
